import React, { useState, useEffect, useMemo } from "react";
import styled from "styled-components";
import * as d3 from "d3";
import { debounce } from "common/api";
import NVStackedAreaChartLive from "common/NVStackedAreaChartLive";
import { formatDate } from "views/Statistics/SubscriberDetail/livemetrics.utils";
import { default as LiveLegend } from "views/Statistics/SubscriberDetail/DPIAnalyticsLiveLegend";
import CurrentDataTable from "./CurrentDataTable";
import { CursorLine, LegendContainer } from "./utils";
import Tooltip from "./Tooltip";

const ChartsWrapper = styled.div`
  display: flex;
  flex-flow: row wrap;
  gap: 10px;
`;

const ChartContainer = styled.div`
  flex: 2 2 50%;
  position: relative;

  @media (max-width: 900px) {
    width: 100%;
  }

  svg.nvd3-svg .nv-axis path.domain {
    stroke-opacity: 0;
  }

  .nv-y.nv-axis .nv-axisMaxMin {
    display: none;
  }

  .nv-y.nv-axis .tick.zero line {
    stroke: transparent !important;
  }

  .nvd3 .nv-groups path.nv-line {
    stroke-width: 1.1;
  }

  .border {
    position: absolute;
    z-index: 99999;

    &.top {
      width: 100%;
      height: 55px;
      min-height: 55px;
      top: -50px;
      left: 0;
    }
    &.right {
      width: 45px;
      height: 100%;
      top: 0;
      right: 5px;
    }

    &.bottom {
      min-height: 70px;
      width: 100%;
      height: 30px;
      bottom: -40px;
      left: 0;
    }

    &.left {
      width: 50px;
      height: 100%;
      top: 0;
      left: 20px;
    }
  }
`;

const TableContainer = styled.div`
  flex: 1 1 440px;
  display: flex;
  @media (max-width: 900px) {
    overflow-x: scroll;
  }
`;

const MainChart = ({
  items,
  fieldsSet,
  fields,
  margin,
  onMouseEvent,
  mouseEvent,
  setRangeMain,
  rangeMain,
  hoverderTime,
  currentData,
  width,
  fieldsMap,
  mouseXMain,
  top20Fields,
  hasRestValue,
  openDashboard,
  type,
  output,
  value,
  total,
  initTime,
  renderRate,
  seconds,
  plotSelection,
  setPlotSelection
}) => {
  const [filterFields, setFilterFields] = useState(new Set());
  const [filteredFields, setFilteredFields] = useState([]);

  function removeTooltip() {
    if (mouseEvent) {
      onMouseEvent(null);
    }
  }

  useEffect(() => {
    if (fieldsSet) {
      setFilterFields(fieldsSet);
    }
  }, [JSON.stringify([...fieldsSet])]);

  useEffect(() => {
    if (filterFields) {
      setFilteredFields(fields.filter((field) => filterFields.has(field.name)));
    }
  }, [JSON.stringify([...filterFields])]);

  const sortedFields = useMemo(() => {
    if (!filteredFields) {
      return [];
    }
    const { rest, otherFields } = filteredFields.reduce(
      (acc, value) => {
        if (value.name === "rest") {
          acc.rest = value;
        } else {
          acc.otherFields.push(value);
        }
        return acc;
      },
      { rest: undefined, otherFields: [] }
    );
    return [
      ...otherFields.sort((a, b) => {
        return a.name.localeCompare(b.name);
      }),
      ...(rest !== undefined ? [rest] : []),
    ];
  }, [JSON.stringify(filteredFields)]);

  const top20FieldsSet = useMemo(
    () =>
      top20Fields.reduce((acc, { name }) => {
        acc.add(name);
        return acc;
      }, new Set()),
    [JSON.stringify(top20Fields)]
  );

  const createMouseEvent = (e) => {
    const newEvent = {
      ...e,
      rangeMain,
      mouseXMain: e.mouseX,
      mouseXExtra: null,
    };
    onMouseEvent(newEvent);
  };

  return (
    <ChartsWrapper>
      <ChartContainer>
        {items.length > 0 ? (
          <LegendContainer>
            <LiveLegend
              fields={top20Fields}
              onChange={(filter) => setFilterFields(filter)}
              value={filterFields}
              rest={
                hasRestValue ? (fieldsMap ? fieldsMap["rest"] : false) : false
              }
              onContextMenu={(name) => openDashboard(name, output)}
              maxWidthField="240"
            />
          </LegendContainer>
        ) : null}
        {mouseEvent && (
          <>
            <div
              className="border top"
              onMouseEnter={removeTooltip}
              onMouseLeave={removeTooltip}
            ></div>
            <div
              className="border right"
              onMouseEnter={removeTooltip}
              onMouseLeave={removeTooltip}
            ></div>
            <div
              className="border bottom"
              onMouseEnter={removeTooltip}
              onMouseLeave={removeTooltip}
            ></div>
            <div
              className="border left"
              onMouseEnter={removeTooltip}
              onMouseLeave={removeTooltip}
            ></div>
          </>
        )}
        <NVStackedAreaChartLive
          items={items}
          xAxisFormat={d3.timeFormat("%m/%d %H:%M")}
          fields={sortedFields}
          yAxisUnits="Mbps"
          yAxisTopGap={0.05}
          margin={margin}
          chartWidth={width - margin.left - margin.top}
          minHeight="276px"
          onHighlight={(e) => {
            if (e) {
              debounce(createMouseEvent(e), 2000);
            }
          }}
          onSeriesInspect={({ name }) => openDashboard(name, output)}
          onRange={(rangeNew) => {
            if (rangeNew !== rangeMain) {
              setRangeMain(rangeNew);
            }
          }}
          renderRate={renderRate}
        />
        {mouseEvent && hoverderTime && mouseXMain && filterFields.size > 0 && (
          <CursorLine
            style={{
              left: mouseXMain + margin.left - 4,
              height: "242px",
              bottom: "30px",
            }}
          />
        )}
        {mouseEvent && hoverderTime && filterFields.size > 0 && (
          <Tooltip
            dataPoint={currentData}
            mouseX={mouseEvent.mouseX}
            mouseY={mouseEvent.mouseY}
            title={formatDate(hoverderTime)}
            mouseEvent={mouseEvent}
            chartWidth={width}
            fieldsMap={fieldsMap}
            filterFields={filterFields}
            type={type}
            unit="Mbps"
            top20={top20FieldsSet}
          />
        )}
      </ChartContainer>
      <TableContainer>
        {filterFields.size > 0 ? (
          <CurrentDataTable
            filterFields={filterFields}
            fields={top20Fields}
            headerOutput={output === "ip-addresses" ? "IP-Addr" : "Subs-Id"}
            type={type}
            current={value}
            onSeriesInspect={(name) => openDashboard(name, output)}
            total={total}
            initTime={initTime}
            seconds={seconds}
            plotSelection={plotSelection}
            setPlotSelection={setPlotSelection}
          />
        ) : null}
      </TableContainer>
    </ChartsWrapper>
  );
};

export default MainChart;
